/**
 * StudLivePreview — podgląd na żywo strony reproduktora (prawa kolumna kreatora reproduktora).
 * Styl spójny 1:1 z LitterLivePreview / DogLivePreview / LivePreview Kreatora Hodowli
 * (KROKI | FORMULARZ | PODGLĄD NA ŻYWO).
 */
import { useState } from 'react';
import { Monitor, Smartphone, MapPin, Tag, PawPrint, Globe, Search, Sparkles, Check, BadgeCheck, Award, Shield } from 'lucide-react';

const AFFILIATION_LABEL = {
  zkwp: 'ZKwP (FCI)',
  fci: 'FCI',
  vdh: 'VDH (FCI)',
  wusv: 'WUSV',
};

export default function StudLivePreview({ form }) {
  const [mobile, setMobile] = useState(false);

  const available = form?.stud_available !== false;
  const st = available
    ? { label: 'Dostępny do krycia', className: 'bg-emerald-50 text-emerald-600' }
    : { label: 'Niedostępny', className: 'bg-zinc-100 text-zinc-500' };

  const location = [form?.city, form?.voivodeship].filter(Boolean).join(', ');
  const registry = AFFILIATION_LABEL[form?.affiliation_type]
    || (form?.affiliation_type === 'other' ? (form?.affiliation_other_name || 'Związek') : null);
  const healthCount = (form?.selected_health_tests || []).length;
  const titlesCount = (form?.selected_championships || []).length + (form?.selected_show_achievements || []).length;

  return (
    <div data-testid="stud-live-preview">
      {/* Pasek statusu podglądu */}
      <div className="mb-3 px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-500 truncate">
              Twoja strona · podgląd na żywo
            </span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200/60">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
            </span>
          </div>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
            <button type="button" onClick={() => setMobile(false)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${!mobile ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`} aria-label="Desktop">
              <Monitor className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setMobile(true)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${mobile ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`} aria-label="Mobile">
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Ecosystem signals strip — 1:1 z Kreatorem Hodowli / Miotu */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="stud-live-preview-ecosystem-signals">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Globe className="w-3 h-3" /> Widoczne publicznie
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium ring-1 ring-blue-200/50">
            <Search className="w-3 h-3" /> Gotowe pod Google
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Sparkles className="w-3 h-3" /> Indeksowane w wyszukiwarkach
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium ring-1 ring-emerald-200/50">
            <Check className="w-3 h-3" /> Mobile-ready
          </span>
        </div>
      </div>

      {/* Karta strony reproduktora */}
      <div className={`mx-auto bg-white rounded-3xl border border-zinc-200 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.25)] overflow-hidden transition-all ${mobile ? 'max-w-[320px]' : 'w-full'}`}>
        {/* Hero / zdjęcie główne */}
        <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-100 to-zinc-100">
          {form?.main_photo ? (
            <img src={form.main_photo} alt={form.name || 'reproduktor'} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-orange-300 gap-2">
              <PawPrint className="w-10 h-10" />
              <span className="text-xs text-zinc-400">Dodaj zdjęcie główne w kroku „Zdjęcia i kontakt"</span>
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.className}`}>{st.label}</span>
            {registry && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/90 text-zinc-700">{registry}</span>
            )}
          </div>
        </div>

        {/* Treść */}
        <div className="p-5 space-y-3">
          <div>
            <h3 className="text-xl font-bold text-zinc-900 leading-tight">{form?.name || 'Imię reproduktora'}</h3>
            <p className="text-sm text-zinc-500">{form?.breed || 'Rasa'}</p>
          </div>

          {location && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-1">{location}</span>
            </div>
          )}

          {registry && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <BadgeCheck className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <span>Rodowód: {registry}</span>
            </div>
          )}

          {titlesCount > 0 && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Award className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <span>{titlesCount} {titlesCount === 1 ? 'tytuł / osiągnięcie' : 'tytuły / osiągnięcia'}</span>
            </div>
          )}

          {healthCount > 0 && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span>{healthCount} {healthCount === 1 ? 'badanie zdrowotne' : 'badań zdrowotnych'}</span>
            </div>
          )}

          {form?.stud_price && (
            <div className="flex items-start gap-2 text-sm text-emerald-700">
              <Tag className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span className="font-semibold">{form.stud_price} · za krycie</span>
            </div>
          )}

          <p className="text-sm text-zinc-500 leading-relaxed line-clamp-3">
            {form?.description || 'Tu pojawi się opis reproduktora — dodasz go w kroku „Zdjęcia i kontakt".'}
          </p>

          <button className="w-full mt-1 h-10 rounded-xl bg-orange-500 text-white text-sm font-semibold">
            {form?.cta_label || 'Zapytaj o krycie'}
          </button>
        </div>

        {/* Reproduktor = niezależna strona promocyjna */}
        <div className="bg-gray-50 border-t border-gray-100 p-3">
          <p className="text-gray-500 text-[10px] text-center mb-1">
            Po opublikowaniu oferta będzie widoczna w:
          </p>
          <p className="text-orange-600 text-[10px] text-center font-medium">
            {['Google', 'Bing', 'Mapy', 'Katalog APISAI', 'Facebook', 'Asystenci'].join(' · ')}
          </p>
        </div>
      </div>

      <p className="text-center text-xs text-zinc-400 mt-3">
        To samodzielna strona reproduktora — możesz ją promować niezależnie od hodowli.
      </p>
    </div>
  );
}
